import React, { useState } from 'react';
import {
  Mic, MicOff, CheckCircle2, AlertTriangle, Lock, RefreshCw,
  ChevronDown, Settings2, Sliders, Volume2
} from 'lucide-react';

export default function MicrophonePreflight({
  auth,
  compact = false,
  className = '',
  onReadyChange,
}) {
  const {
    permission,
    isReady,
    devices,
    selectedDeviceId,
    audioLevel,
    errorMsg,
    requestPermission,
    recheckPermission,
    selectDevice,
  } = auth;

  const [requesting, setRequesting] = useState(false);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);

  const handleAllowClick = async () => {
    setRequesting(true);
    try {
      await requestPermission();
    } finally {
      setRequesting(false);
    }
  };

  // State 1: Permission Granted — show ready indicator, audio meter, and mic selector
  if (isReady) {
    const selectedDevice = devices.find((d) => d.deviceId === selectedDeviceId) || devices[0];
    const displayLabel = selectedDevice?.label || 'Default Microphone';

    if (compact) {
      return (
        <div className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs ${className}`}>
          <div className="flex items-center gap-1.5 font-medium">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span>Mic Ready</span>
          </div>

          {/* Mini audio level meter */}
          <div className="w-12 h-2 bg-black/40 rounded-full overflow-hidden flex items-center px-0.5" title={`Input level: ${audioLevel}%`}>
            <div
              className="h-1 rounded-full bg-linear-to-r from-emerald-500 to-emerald-300 transition-all duration-75"
              style={{ width: `${Math.max(8, audioLevel)}%` }}
            />
          </div>

          {devices.length > 1 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDeviceDropdown(!showDeviceDropdown)}
                className="flex items-center gap-1 text-[11px] text-white/70 hover:text-white px-1.5 py-0.5 rounded bg-white/5 hover:bg-white/10 transition"
                title="Change microphone"
              >
                <span className="max-w-[100px] truncate">{displayLabel}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {showDeviceDropdown && (
                <div className="absolute right-0 top-6 z-50 min-w-[200px] bg-civix-panel border border-civix-border rounded-lg shadow-2xl p-1 text-xs">
                  <div className="px-2 py-1 text-[10px] uppercase font-semibold text-white/40">Select Microphone</div>
                  {devices.map((d) => (
                    <button
                      key={d.deviceId}
                      type="button"
                      onClick={() => {
                        selectDevice(d.deviceId);
                        setShowDeviceDropdown(false);
                      }}
                      className={`w-full text-left px-2 py-1.5 rounded flex items-center justify-between transition ${
                        d.deviceId === selectedDeviceId ? 'bg-civix-purple/30 text-white font-medium' : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <span className="truncate">{d.label}</span>
                      {d.deviceId === selectedDeviceId && <CheckCircle2 className="w-3.5 h-3.5 text-civix-purpleSoft shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`p-3 rounded-xl bg-linear-to-r from-emerald-950/40 via-civix-panel to-emerald-950/20 border border-emerald-500/30 flex flex-wrap items-center justify-between gap-3 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-400">
            <Mic className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Microphone Ready
              </span>
              <span className="text-[11px] text-white/40">Softphone armed</span>
            </div>
            <div className="text-xs text-white/80 font-medium truncate max-w-[280px]">
              {displayLabel}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Audio Visualizer Level Bar */}
          <div className="flex items-center gap-2 bg-black/40 px-3 py-1.5 rounded-lg border border-white/5">
            <Volume2 className={`w-3.5 h-3.5 ${audioLevel > 15 ? 'text-emerald-400 animate-bounce' : 'text-white/30'}`} />
            <div className="w-24 h-2 bg-white/10 rounded-full overflow-hidden flex items-center px-0.5">
              <div
                className="h-1 rounded-full bg-linear-to-r from-emerald-500 via-teal-400 to-cyan-300 transition-all duration-75"
                style={{ width: `${Math.max(6, audioLevel)}%` }}
              />
            </div>
            <span className="text-[10px] font-mono text-white/50 w-6 text-right tabular-nums">{audioLevel}%</span>
          </div>

          {/* Device Selector */}
          {devices.length > 1 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDeviceDropdown(!showDeviceDropdown)}
                className="btn-ghost text-xs flex items-center gap-1.5 py-1.5 px-2.5 border border-white/10"
              >
                <Sliders className="w-3.5 h-3.5 text-white/60" />
                <span className="max-w-[130px] truncate">{displayLabel}</span>
                <ChevronDown className="w-3 h-3 text-white/50" />
              </button>
              {showDeviceDropdown && (
                <div className="absolute right-0 top-9 z-50 min-w-[240px] bg-civix-panel border border-civix-border rounded-lg shadow-2xl p-1 text-xs">
                  <div className="px-2.5 py-1 text-[10px] uppercase font-semibold text-white/40">Audio Input Device</div>
                  {devices.map((d) => (
                    <button
                      key={d.deviceId}
                      type="button"
                      onClick={() => {
                        selectDevice(d.deviceId);
                        setShowDeviceDropdown(false);
                      }}
                      className={`w-full text-left px-2.5 py-2 rounded flex items-center justify-between transition ${
                        d.deviceId === selectedDeviceId ? 'bg-civix-purple/30 text-white font-medium' : 'text-white/70 hover:bg-white/5 hover:text-white'
                      }`}
                    >
                      <span className="truncate pr-2">{d.label}</span>
                      {d.deviceId === selectedDeviceId && <CheckCircle2 className="w-4 h-4 text-civix-purpleSoft shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // State 2: Permission Denied / Blocked
  if (permission === 'denied') {
    return (
      <div className={`p-4 rounded-xl bg-red-950/30 border border-red-500/40 text-white ${className}`}>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-red-500/20 border border-red-500/30 flex items-center justify-center text-red-400 shrink-0 mt-0.5">
            <MicOff className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-red-300">Microphone Access Blocked</h3>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-red-500/20 text-red-300 font-semibold">
                Dialing Disabled
              </span>
            </div>
            <p className="text-xs text-white/70 mt-1">
              Your browser is blocking microphone access for CIVIX. To enable zero-friction softphone and live audio:
            </p>

            <div className="mt-3 p-2.5 rounded-lg bg-black/40 border border-white/5 text-xs text-white/80 space-y-1.5">
              <div className="flex items-center gap-2 font-medium">
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-white/10 text-[10px]">1</span>
                <span>Click the <strong>Lock</strong> / <strong>Tune</strong> icon <Lock className="w-3.5 h-3.5 inline mx-1 text-amber-300" /> on the left side of your browser URL bar.</span>
              </div>
              <div className="flex items-center gap-2 font-medium">
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-white/10 text-[10px]">2</span>
                <span>Toggle <strong>Microphone</strong> to <strong>Allow</strong>.</span>
              </div>
              <div className="flex items-center gap-2 font-medium">
                <span className="flex items-center justify-center w-4 h-4 rounded-full bg-white/10 text-[10px]">3</span>
                <span>Click <strong>"Re-check Permission"</strong> below.</span>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button
                type="button"
                onClick={() => recheckPermission()}
                className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-500 transition flex items-center gap-1.5 shadow-md shadow-red-900/30"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Re-check Permission
              </button>
              <button
                type="button"
                onClick={handleAllowClick}
                disabled={requesting}
                className="btn-ghost text-xs py-1.5 px-3"
              >
                {requesting ? 'Prompting…' : 'Try Prompting Again'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // State 3: Permission Prompt or Unknown — Inline Pre-Flight Banner
  return (
    <div className={`p-4 rounded-xl bg-linear-to-r from-civix-purple/20 via-civix-panel to-civix-cyan/15 border border-civix-purple/40 shadow-lg ${className}`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-linear-to-br from-civix-purple to-civix-cyan flex items-center justify-center text-white shrink-0 shadow-md shadow-civix-purple/30">
            <Mic className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              Enable Microphone to Start Dialing
              <span className="text-[10px] uppercase font-semibold text-civix-cyan tracking-wider px-2 py-0.5 rounded-full bg-civix-cyan/10 border border-civix-cyan/20">
                1-Click Pre-Flight
              </span>
            </h3>
            <p className="text-xs text-white/70 mt-0.5 max-w-xl">
              CIVIX requires microphone access for live calling and browser softphone audio. Grant access once to unlock uninterrupted, zero-click power dialing.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleAllowClick}
          disabled={requesting}
          className="w-full sm:w-auto px-4 py-2.5 rounded-lg text-xs font-bold text-white
                     bg-linear-to-r from-[#7C3AED] to-[#E040FB] hover:shadow-lg hover:shadow-[#7C3AED]/50
                     transition flex items-center justify-center gap-2 shrink-0 cursor-pointer disabled:opacity-50"
        >
          <Mic className="w-4 h-4" />
          {requesting ? 'Requesting Access…' : 'Allow Microphone Access'}
        </button>
      </div>
    </div>
  );
}
